import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import {
  createLeadForm,
  deleteLeadForm,
  generateHtml,
  getLeadForms,
  updateLeadForm,
} from "../../../actionCreator/webToLeadActionCreater";
import { useAlert } from "../../../context/AlertProvider";
import { PAGE_LENGTH } from "../../../config";
import { getUserInfo } from "../../../utils/utils";
import { configType } from "../../../constant/WebToLead";

export const useLeadFormActions = (handleError, callback) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = getUserInfo();
  const { showAlert } = useAlert();
  const leadForms = useSelector((state) => state.WebToLeadReducer.leadForms);
  const loading = useSelector((state) => state.WebToLeadReducer.loading);

  const getLeadFormsList = async (page) => {
    const url = `sort=createdDate,desc&page=${page - 1}&size=${PAGE_LENGTH}`;
    await dispatch(getLeadForms(url));
  };

  const handleCreate = async (payload) => {
    const res = await dispatch(createLeadForm(payload));
    if (res?.data?.configType !== configType.SHARED_URL) {
      dispatch(
        generateHtml(
          res?.data?.id,
          res?.data?.configType === configType.EMBEDDED_SCRIPT
        )
      );
      showAlert("success", `${res?.data?.formName} Form created successfully.`);
    }
    if (res?.response?.data?.errorKey === "duplicate-formname") {
      handleError(res?.response?.data?.title);
      showAlert("error", res?.response?.data?.title);
    }
    if (res?.data?.configType === configType.SHARED_URL) {
      navigate("/settings/web-to-lead");
    }

    if (res?.response?.data?.errorKey === "expired_grace_period") {
      callback && callback();
      return;
    }
    if (res?.response?.data?.errorKey === "expired") {
      showAlert("error", res?.response?.data?.title);
    }
    if (res?.response?.status === 403) {
      showAlert(
        "warning",
        "Permission Denied",
        "You do not have permission to create web to lead form."
      );
    }
  };

  const handleClone = async (payload, page, closeModal) => {
    const res = await dispatch(createLeadForm(payload));
    if (res?.response?.data?.errorKey === "duplicate-formname") {
      return { status: "error", message: res?.response?.data?.title };
    }
    if (res?.data?.configType) {
      getLeadFormsList(page);
      showAlert("success", `${payload?.formName} Form created successfully.`);
      return res;
    }
    if (res?.response?.data?.errorKey === "expired_grace_period") {
      callback && callback();
      return;
    }
    if (res?.response?.data?.errorKey === "expired") {
      showAlert("error", res?.response?.data?.title);
    }
    if (res?.response?.status === 403) {
      showAlert(
        "warning",
        "Permission Denied",
        "You do not have permission to clone web to lead form."
      );
      closeModal && closeModal();
    }
  };

  const handleDelete = async (row, page) => {
    const res = await dispatch(deleteLeadForm({ id: row?.id }));
    if (res?.status === 204) {
      showAlert("success", `${row?.formName} Form Deleted successfully.`);
      getLeadFormsList(page);
    } else {
      if (res?.response?.data?.errorKey === "expired_grace_period") {
        callback && callback();
        return;
      }
      if (res?.response?.data?.errorKey === "expired") {
        showAlert("error", res?.response?.data?.title);
      } else {
        if (res?.response?.status === 403) {
          showAlert(
            "warning",
            "Permission Denied",
            "You do not have permission to delete web to lead form."
          );
        } else {
          showAlert("error", "Something went wrong. Please try again.");
        }
      }
    }
  };

  const handleStatus = async (row, page) => {
    const res = await dispatch(
      updateLeadForm({
        id: row?.id,
        force: row?.force,
        active: !row?.active,
        updatedDate: new Date(),
        userId: user?.id,
      })
    );

    if (res?.status === 200) {
      getLeadFormsList(page);
      showAlert(
        "success",
        `${row?.formName} has been successfully ${!row?.active ? "activated" : "inactivated"}.`
      );
    } else {
      if (res?.response?.data?.errorKey === "expired_grace_period") {
        callback && callback();
        return;
      }
      if (res?.response?.data?.errorKey === "expired") {
        showAlert("error", res?.response?.data?.title);
      } else {
        if (res?.response?.status === 403) {
          showAlert(
            "warning",
            "Permission Denied",
            "You do not have permission to perform this action."
          );
        } else {
          showAlert("error", "Something went wrong. Please try again.");
        }
      }
    }
  };

  const handleUpdate = async (payload, page, isEdit) => {
    const res = await dispatch(
      updateLeadForm({
        ...payload,
        id: payload?.id,
        updatedDate: new Date(),
        userId: user?.id,
        formName: payload?.formName,
        webToLeadConfigFields: payload?.webToLeadConfigFields,
      })
    );
    if (res?.status === 200) {
      getLeadFormsList(page);
      if (res?.data?.configType !== configType.SHARED_URL && isEdit) {
        dispatch(
          generateHtml(
            res?.data?.id,
            res?.data?.configType === configType.EMBEDDED_SCRIPT
          )
        );
      }
      showAlert(
        "success",
        `${payload?.formName} has been successfully updated.`
      );
    } else {
      if (res?.response?.data?.errorKey === "expired_grace_period") {
        callback && callback();
        return;
      }
      if (res?.response?.data?.errorKey === "expired") {
        showAlert("error", res?.response?.data?.title);
      } else {
        if (res?.response?.status === 403) {
          showAlert(
            "warning",
            "Permission Denied",
            "You do not have permission to update this form."
          );
        } else {
          showAlert("error", "Something went wrong. Please try again.");
        }
      }
    }
  };

  return {
    handleDelete,
    handleStatus,
    leadForms,
    handleCreate,
    loading,
    getLeadFormsList,
    handleClone,
    handleUpdate,
  };
};
